import React, { useState } from 'react'
import { useNavigate, useLocation, Navigate } from 'react-router-dom'
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import { AppVersionText } from '../layout/AppVersionText'
import crcEvansLogo from '../../assets/images/crc-evans-logo.png'

const inputClass =
  'block w-full rounded-lg border border-[#E2E5EA] bg-white px-3 py-2 text-sm text-text-grey placeholder:text-heritage-grey hover:border-[#cfd4dc] focus:border-blueprint-blue focus:outline-none disabled:opacity-60'

export function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpError, setOtpError] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated, mfaRequired, completeMfaSignIn, resendMfaOtp, cancelMfa } = useAuth()

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/'

  if (isAuthenticated) {
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    if (!username.trim() || !password) {
      setError('Please enter both email and password')
      setLoading(false)
      return
    }

    try {
      const res = await login(username.trim(), password)
      if (res.success && !res.mfaRequired) {
        navigate(from, { replace: true })
      }
    } catch (err: unknown) {
      const ex = err as { code?: string; message?: string; name?: string }
      let message = 'An error occurred during login. Please try again.'
      if (ex.code === 'NotAuthorizedException') message = 'Incorrect email or password.'
      else if (ex.code === 'UserNotConfirmedException') message = 'Your account is not confirmed. Please check your email.'
      else if (ex.code === 'PasswordResetRequiredException') message = 'Password reset is required. Please contact administrator.'
      else if (ex.code === 'UserNotFoundException') message = 'User not found. Please check your email.'
      else if (ex.code === 'InvalidParameterException') message = ex.message || 'Invalid sign-in. Your User Pool may require username instead of email, or the app client auth flow may be misconfigured.'
      else if (ex.message) message = ex.message
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setOtpError('')
    setResendMessage(null)
    const trimmedCode = otpCode.trim()
    if (trimmedCode.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP.')
      return
    }
    setLoading(true)
    const result = await completeMfaSignIn(trimmedCode)
    if (result.success) {
      navigate(from, { replace: true })
    } else {
      setOtpError(result.error || 'Invalid OTP code. Please try again.')
      setOtpCode('')
    }
    setLoading(false)
  }

  const handleResendOtp = async () => {
    setIsResending(true)
    setOtpError('')
    setResendMessage(null)
    try {
      await resendMfaOtp()
      setResendMessage('A new OTP has been sent to your email.')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to resend code'
      setOtpError(message)
    } finally {
      setIsResending(false)
    }
  }

  const handleCancelMfa = () => {
    cancelMfa()
    setOtpCode('')
    setOtpError('')
    setResendMessage(null)
    setPassword('')
  }

  if (mfaRequired) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-page-bg py-12 px-4 font-sans antialiased">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-sm p-8 space-y-6 sm:px-9">
            <header className="text-center space-y-4">
              <img src={crcEvansLogo} alt="CRC Evans" className="h-9 w-auto mx-auto object-contain" />
              <div>
                <h1 className="font-heading text-2xl font-semibold text-blueprint-blue">
                  WPS Studio
                </h1>
                <p className="mt-1 text-sm text-heritage-grey">
                  Security Verification
                </p>
              </div>
            </header>

            <form onSubmit={handleVerifyOtp} className="space-y-5" noValidate>
              {otpError && (
                <div className="bg-[rgba(196,0,31,0.06)] text-readable-red px-4 py-3 rounded-lg text-sm" role="alert">
                  {otpError}
                </div>
              )}
              {resendMessage && (
                <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 text-[13px] leading-snug text-green-800" role="status">
                  {resendMessage}
                </div>
              )}

              <div className="space-y-1">
                <label className="block text-sm font-medium text-text-grey" htmlFor="otp-code">
                  Verification code
                </label>
                <input
                  id="otp-code"
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={loading}
                  autoComplete="one-time-code"
                  autoFocus
                  className={inputClass}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg border border-transparent bg-hero-red py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-hero-red-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Verifying...' : 'Verify'}
              </button>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handleCancelMfa}
                  disabled={loading}
                  className="text-sm text-heritage-grey hover:text-text-grey transition-colors disabled:opacity-60"
                >
                  ← Back to login
                </button>
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending || loading}
                  className="text-sm font-semibold text-hero-red hover:underline disabled:opacity-60"
                >
                  {isResending ? 'Sending...' : 'Resend code'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="mt-7 text-center text-[12px] text-heritage-grey">
          © {new Date().getFullYear()} WPS Studio. All rights reserved.
          <div className="mt-1">
            <AppVersionText />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-page-bg py-12 px-4 font-sans antialiased">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-[#E2E5EA] shadow-sm p-8 space-y-6 sm:px-9">
          <header className="text-center space-y-4">
            <img src={crcEvansLogo} alt="CRC Evans" className="h-9 w-auto mx-auto object-contain" />
            <div>
              <h1 className="font-heading text-2xl font-semibold text-blueprint-blue">
                WPS Studio
              </h1>
              <p className="mt-1 text-sm text-heritage-grey">
                Sign in to your account
              </p>
            </div>
          </header>

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {error && (
              <div className="bg-[rgba(196,0,31,0.06)] text-readable-red px-4 py-3 rounded-lg text-sm" role="alert">
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text-grey" htmlFor="username">
                Email address
              </label>
              <input
                id="username"
                name="username"
                type="email"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={loading}
                placeholder="you@crce.com"
                className={inputClass}
              />
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-text-grey" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  placeholder="Enter password"
                  className={`${inputClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  disabled={loading}
                  className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-heritage-grey hover:bg-[#EEF0F3] hover:text-text-grey focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-1 disabled:opacity-50"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeSlashIcon className="h-5 w-5 shrink-0" aria-hidden /> : <EyeIcon className="h-5 w-5 shrink-0" aria-hidden />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg border border-transparent bg-hero-red py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-hero-red-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-hero-red focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>

      <div className="mt-7 text-center text-[12px] text-heritage-grey">
        © {new Date().getFullYear()} WPS Studio. All rights reserved.
        <div className="mt-1">
          <AppVersionText />
        </div>
      </div>
    </div>
  )
}
